import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent } from "react";
import { useParams } from "@/lib/router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ReactFlow,
  Controls,
  MiniMap,
  Background,
  BackgroundVariant,
  addEdge,
  useNodesState,
  useEdgesState,
  useReactFlow,
  ReactFlowProvider,
  type Connection,
  type Node,
  type Edge,
  type NodeTypes,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { graphsApi } from "../api/graphs";
import { usePractice } from "../context/PracticeContext";
import { useBreadcrumbs } from "../context/BreadcrumbContext";
import { queryKeys } from "../lib/queryKeys";
import { PageSkeleton } from "../components/PageSkeleton";
import {
  AgentRefNode,
  DecisionNode,
  EhrActionNode,
  NotificationNode,
  HumanReviewNode,
  TimerNode,
  DataTransformNode,
  ApiCallNode,
  ComplianceNode,
  PayerActionNode,
  WorkflowTriggerNode,
  WfStartNode,
  WfEndNode,
} from "../components/workflow-studio/WorkflowNodes";
import { WorkflowNodeConfig } from "../components/workflow-studio/WorkflowNodeConfig";
import { EdgeConfigDrawer } from "../components/studio/EdgeConfigDrawer";
import { WorkflowPalette, getDefaultWfData } from "../components/workflow-studio/WorkflowPalette";
import { Button } from "@/components/ui/button";
import {
  Save,
  GitBranch,
  Rocket,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Bot,
  GitFork,
  Hand,
  Shield,
  Workflow,
  Play,
  Loader2,
} from "lucide-react";
import { cn } from "../lib/utils";
import { BuildAgentPanel } from "../components/studio/BuildAgentPanel";
import { useGraphRun } from "../hooks/useGraphRun";

const nodeTypes: NodeTypes = {
  agent_ref: AgentRefNode,
  decision: DecisionNode,
  ehr_action: EhrActionNode,
  notification: NotificationNode,
  human_review: HumanReviewNode,
  timer: TimerNode,
  data_transform: DataTransformNode,
  api_call: ApiCallNode,
  compliance: ComplianceNode,
  payer_action: PayerActionNode,
  wf_trigger: WorkflowTriggerNode,
  wf_start: WfStartNode,
  wf_end: WfEndNode,
};

const NODE_LEGEND = [
  { label: "Agent", color: "bg-violet-500" },
  { label: "Decision", color: "bg-amber-500" },
  { label: "EHR", color: "bg-emerald-500" },
  { label: "Payer", color: "bg-blue-500" },
  { label: "Notify", color: "bg-orange-500" },
  { label: "HITL", color: "bg-rose-500" },
  { label: "Compliance", color: "bg-red-500" },
];

let nodeIdCounter = 0;
function generateNodeId(type: string) {
  nodeIdCounter += 1;
  return `${type}_${nodeIdCounter}`;
}

function WorkflowBuilderInner() {
  const { workflowId } = useParams<{ workflowId: string }>();
  const { selectedPracticeId } = usePractice();
  const { setBreadcrumbs } = useBreadcrumbs();
  const queryClient = useQueryClient();
  const reactFlowInstance = useReactFlow();
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
  const [paletteCollapsed, setPaletteCollapsed] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [buildPanelOpen, setBuildPanelOpen] = useState(false);
  const reactFlowWrapper = useRef<HTMLDivElement>(null);

  const { data: graph, isLoading } = useQuery({
    queryKey: queryKeys.graphs.detail(workflowId!),
    queryFn: () => graphsApi.get(workflowId!),
    enabled: !!workflowId,
  });

  const initialNodes = useMemo(() => {
    if (!graph?.graphDefinition?.nodes) return [];
    return graph.graphDefinition.nodes.map((n) => ({
      id: n.id,
      type: n.type,
      position: n.position,
      data: n.data,
    }));
  }, [graph]);

  const initialEdges = useMemo(() => {
    if (!graph?.graphDefinition?.edges) return [];
    return graph.graphDefinition.edges.map((e) => ({
      id: e.id,
      source: e.source,
      target: e.target,
      label: e.label,
      data: { condition: e.condition },
      animated: true,
      style: { stroke: "var(--primary)", strokeWidth: 2 },
    }));
  }, [graph]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  useEffect(() => {
    if (initialNodes.length > 0) setNodes(initialNodes);
  }, [initialNodes, setNodes]);
  useEffect(() => {
    if (initialEdges.length > 0) setEdges(initialEdges);
  }, [initialEdges, setEdges]);

  useEffect(() => {
    setBreadcrumbs([
      { label: "Workflow Studio", href: "/workflows" },
      { label: graph?.name ?? "Loading..." },
    ]);
  }, [setBreadcrumbs, graph?.name]);

  // ── Validation ─────────────────────────────────────────────────
  const validation = useMemo(() => {
    const errors: string[] = [];
    const warnings: string[] = [];
    const hasStart = nodes.some((n) => n.type === "wf_start");
    const hasEnd = nodes.some((n) => n.type === "wf_end");
    if (!hasStart) errors.push("Workflow needs a Start node");
    if (!hasEnd) errors.push("Workflow needs an End node");

    // Check for unconnected nodes
    const connectedIds = new Set<string>();
    for (const e of edges) {
      connectedIds.add(e.source);
      connectedIds.add(e.target);
    }
    for (const n of nodes) {
      if (n.type === "wf_start" || n.type === "wf_end") continue;
      if (!connectedIds.has(n.id)) warnings.push(`"${(n.data as Record<string, unknown>).name ?? n.id}" is not connected`);
    }

    // Check agent_ref nodes have agents selected
    for (const n of nodes) {
      if (n.type === "agent_ref" && !(n.data as Record<string, unknown>).agentId) {
        warnings.push(`"${(n.data as Record<string, unknown>).name ?? "Agent Step"}" needs an agent selected`);
      }
    }

    // Check HITL nodes
    const hitlCount = nodes.filter((n) => n.type === "human_review").length;
    if (hitlCount === 0 && nodes.length > 4) {
      warnings.push("Consider adding a Human Review (HITL) node for compliance");
    }

    return { errors, warnings, hasErrors: errors.length > 0, hasWarnings: warnings.length > 0 };
  }, [nodes, edges]);

  const onConnect = useCallback(
    (params: Connection) => {
      setEdges((eds) =>
        addEdge(
          { ...params, animated: true, style: { stroke: "var(--primary)", strokeWidth: 2 } },
          eds,
        ),
      );
    },
    [setEdges],
  );

  const saveMutation = useMutation({
    mutationFn: () =>
      graphsApi.update(workflowId!, {
        graphDefinition: {
          nodes: nodes.map((n) => ({
            id: n.id,
            type: n.type as any,
            position: n.position,
            data: n.data as Record<string, unknown>,
          })),
          edges: edges.map((e) => ({
            id: e.id,
            source: e.source,
            target: e.target,
            label: typeof e.label === "string" ? e.label : undefined,
            condition: (e.data as Record<string, unknown>)?.condition as string | undefined,
          })),
          config: graph?.graphDefinition?.config ?? {},
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.graphs.detail(workflowId!) });
    },
  });

  const isDeployed = graph?.status === "deployed";

  const deployMutation = useMutation({
    mutationFn: () => graphsApi.deploy(workflowId!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.graphs.detail(workflowId!) });
      void queryClient.invalidateQueries({ queryKey: ["workflow-graphs", selectedPracticeId] });
    },
  });

  const undeployMutation = useMutation({
    mutationFn: () => graphsApi.undeploy(workflowId!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.graphs.detail(workflowId!) });
      void queryClient.invalidateQueries({ queryKey: ["workflow-graphs", selectedPracticeId] });
    },
  });

  // Queue-driven test run of the workflow. Saves the current canvas first (so the
  // run executes exactly what's on screen), then starts the run and polls status.
  const graphRun = useGraphRun(selectedPracticeId);
  const handleRun = useCallback(async () => {
    if (!workflowId) return;
    await saveMutation.mutateAsync();
    void graphRun.run(workflowId);
  }, [workflowId, saveMutation, graphRun]);

  // Drag and drop from palette
  const onDragOver = useCallback((event: DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback(
    (event: DragEvent) => {
      event.preventDefault();
      const type = event.dataTransfer.getData("application/reactflow-type");
      if (!type) return;

      const dataStr = event.dataTransfer.getData("application/reactflow-data");
      const data = dataStr ? JSON.parse(dataStr) : {};
      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      const newNode = { id: generateNodeId(type), type, position, data };
      setNodes((nds) => [...nds, newNode]);
    },
    [reactFlowInstance, setNodes],
  );

  const selectedNode = nodes.find((n) => n.id === selectedNodeId) ?? null;
  const selectedEdge = edges.find((e) => e.id === selectedEdgeId) ?? null;

  const updateNodeData = useCallback(
    (nodeId: string, data: Record<string, unknown>) => {
      setNodes((nds) =>
        nds.map((n) => (n.id === nodeId ? { ...n, data: { ...n.data, ...data } } : n)),
      );
    },
    [setNodes],
  );

  const updateEdgeData = useCallback(
    (edgeId: string, data: { label?: string; condition?: string }) => {
      setEdges((eds) =>
        eds.map((e) => {
          if (e.id !== edgeId) return e;
          return {
            ...e,
            label: data.label !== undefined ? data.label : e.label,
            data: { ...(e.data as Record<string, unknown>), condition: data.condition },
          };
        }),
      );
    },
    [setEdges],
  );

  const deleteEdge = useCallback(
    (edgeId: string) => {
      setEdges((eds) => eds.filter((e) => e.id !== edgeId));
      setSelectedEdgeId(null);
    },
    [setEdges],
  );

  if (isLoading) return <PageSkeleton variant="detail" />;

  return (
    <div className="flex flex-col h-[calc(100vh-7rem)] -m-4 md:-m-6">
      {/* Toolbar */}
      <div className="flex items-center gap-1 px-3 py-1.5 border-b border-border bg-card/50 shrink-0">
        <div className="flex items-center gap-1.5 text-xs font-medium text-foreground mr-3">
          <GitBranch className="h-3.5 w-3.5 text-primary" />
          <span className="truncate max-w-[200px]">{graph?.name ?? "Workflow"}</span>
        </div>

        {/* Validation */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowValidation(!showValidation)}
            className={cn(
              "flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium transition-colors",
              validation.hasErrors
                ? "text-destructive bg-destructive/10"
                : validation.hasWarnings
                  ? "text-amber-600 bg-amber-50 dark:text-amber-400 dark:bg-amber-900/20"
                  : "text-emerald-600 bg-emerald-50 dark:text-emerald-400 dark:bg-emerald-900/20",
            )}
          >
            {validation.hasErrors ? (
              <XCircle className="h-3 w-3" />
            ) : validation.hasWarnings ? (
              <AlertTriangle className="h-3 w-3" />
            ) : (
              <CheckCircle2 className="h-3 w-3" />
            )}
            {validation.errors.length + validation.warnings.length > 0
              ? `${validation.errors.length + validation.warnings.length} issues`
              : "Valid"}
          </button>
          {showValidation && (validation.hasErrors || validation.hasWarnings) && (
            <div className="absolute top-full left-0 mt-1 z-50 w-72 rounded-lg border border-border bg-card shadow-lg p-3 space-y-1.5">
              {validation.errors.map((e, i) => (
                <div key={i} className="flex items-start gap-1.5 text-xs text-destructive">
                  <XCircle className="h-3 w-3 mt-0.5 shrink-0" /> {e}
                </div>
              ))}
              {validation.warnings.map((w, i) => (
                <div key={i} className="flex items-start gap-1.5 text-xs text-amber-600 dark:text-amber-400">
                  <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" /> {w}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex-1" />

        {/* Legend */}
        <div className="hidden lg:flex items-center gap-2 mr-3">
          {NODE_LEGEND.map((item) => (
            <span key={item.label} className="flex items-center gap-1 text-[9px] text-muted-foreground">
              <span className={cn("h-2 w-2 rounded-full", item.color)} />
              {item.label}
            </span>
          ))}
        </div>

        {/* Actions */}
        {/* Build Agent toggle */}
        <button
          type="button"
          onClick={() => setBuildPanelOpen(!buildPanelOpen)}
          className={cn(
            "flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium transition-colors",
            buildPanelOpen
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:text-foreground hover:bg-accent/50",
          )}
          title="AI Build Agent"
        >
          <Bot className="h-3 w-3" />
          Build
        </button>

        <Button
          variant="outline"
          size="sm"
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending}
          className="h-7 text-xs gap-1"
        >
          <Save className="h-3 w-3" />
          {saveMutation.isPending ? "Saving..." : "Save"}
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={() => void handleRun()}
          disabled={graphRun.status === "running" || saveMutation.isPending || validation.hasErrors}
          className="h-7 text-xs gap-1"
          title="Save and run this workflow"
        >
          {graphRun.status === "running" ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Play className="h-3 w-3" />
          )}
          {graphRun.status === "running" ? "Running..." : "Run"}
        </Button>
        {graphRun.status !== "idle" && graphRun.status !== "running" && (
          <span
            className={cn(
              "text-[10px] font-medium tabular-nums",
              graphRun.status === "succeeded"
                ? "text-emerald-500"
                : graphRun.status === "interrupted"
                  ? "text-amber-600"
                  : "text-red-500",
            )}
            title={graphRun.runId ? `Run ${graphRun.runId}` : undefined}
          >
            {graphRun.status === "succeeded"
              ? "Run succeeded"
              : graphRun.status === "interrupted"
                ? "Awaiting review"
                : `Run failed${graphRun.error ? `: ${graphRun.error}` : ""}`}
          </span>
        )}

        {isDeployed ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => undeployMutation.mutate()}
            disabled={undeployMutation.isPending}
            className="h-7 text-xs gap-1"
          >
            {undeployMutation.isPending ? "..." : "Undeploy"}
          </Button>
        ) : (
          <Button
            size="sm"
            onClick={() => deployMutation.mutate()}
            disabled={deployMutation.isPending || validation.hasErrors}
            className="h-7 text-xs gap-1"
          >
            <Rocket className="h-3 w-3" />
            {deployMutation.isPending ? "Deploying..." : "Deploy"}
          </Button>
        )}
      </div>

      {/* Canvas */}
      <div className="flex flex-1 min-h-0">
        <WorkflowPalette collapsed={paletteCollapsed} onToggle={() => setPaletteCollapsed(!paletteCollapsed)} />

        <div ref={reactFlowWrapper} className="flex-1">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onDragOver={onDragOver}
            onDrop={onDrop}
            nodeTypes={nodeTypes}
            onNodeClick={(_, node) => {
              setSelectedNodeId(node.id);
              setSelectedEdgeId(null);
            }}
            onEdgeClick={(_, edge) => {
              setSelectedEdgeId(edge.id);
              setSelectedNodeId(null);
            }}
            onPaneClick={() => {
              setSelectedNodeId(null);
              setSelectedEdgeId(null);
            }}
            fitView
            deleteKeyCode="Delete"
            className="bg-background"
          >
            <Controls position="bottom-left" className="!bg-card !border-border !shadow-sm [&>button]:!bg-card [&>button]:!border-border [&>button]:!fill-foreground" />
            <MiniMap
              className="!bg-card !border-border"
              nodeStrokeColor="var(--primary)"
              nodeColor="var(--primary)"
              maskColor="rgba(0,0,0,0.15)"
            />
            <Background variant={BackgroundVariant.Dots} gap={16} size={1} color="var(--border)" />
          </ReactFlow>
        </div>

        {/* Right panel: Build Agent (takes priority when open) */}
        {buildPanelOpen ? (
          <div className="w-80 shrink-0 border-l border-border flex flex-col">
            <BuildAgentPanel
              graphId={workflowId!}
              nodes={nodes}
              edges={edges}
              setNodes={setNodes as any}
              setEdges={setEdges as any}
              mode="workflow"
            />
          </div>
        ) : (
          <>
            {selectedNode && (
              <WorkflowNodeConfig
                node={selectedNode}
                onUpdate={(data) => updateNodeData(selectedNode.id, data)}
                onClose={() => setSelectedNodeId(null)}
              />
            )}
            {selectedEdge && !selectedNode && (
              <EdgeConfigDrawer
                edge={selectedEdge}
                onUpdate={(data) => updateEdgeData(selectedEdge.id, data)}
                onDelete={() => deleteEdge(selectedEdge.id)}
                onClose={() => setSelectedEdgeId(null)}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

export function WorkflowBuilder() {
  return (
    <ReactFlowProvider>
      <WorkflowBuilderInner />
    </ReactFlowProvider>
  );
}
